'use client'

import { useState, useEffect, useRef } from 'react'
import { io, Socket } from 'socket.io-client'

interface Message {
  id: string
  sender: 'CUSTOMER' | 'MERCHANT' | 'AI'
  content: string
  createdAt: string
}

interface WidgetConfig {
  isEnabled: boolean
  position: string
  primaryColor: string
  greeting: string
  offlineMessage: string
  preChatFormEnabled: boolean
  isOnline?: boolean
}

interface Props {
  storeId: string
  apiUrl: string
  config: WidgetConfig
}

export function ChatWidget({ storeId, apiUrl, config }: Props) {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [visitorName, setVisitorName] = useState('')
  const [visitorEmail, setVisitorEmail] = useState('')
  const [prechatDone, setPrechatDone] = useState(!config.preChatFormEnabled)
  const [connecting, setConnecting] = useState(false)

  const socketRef = useRef<Socket | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // [CHAT DEBUG] — remove after widget is confirmed working in production
  useEffect(() => {
  }, [])

  // Restore session from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`chat_session_${storeId}`)
      if (saved) {
        const { conversationId: cid, visitorName: vn, visitorEmail: ve } = JSON.parse(saved)
        if (cid) {
          setConversationId(cid)
          setPrechatDone(true)
          setVisitorName(vn ?? '')
          setVisitorEmail(ve ?? '')
        }
      }
    } catch { /* ignore */ }
  }, [storeId])

  // Load message history whenever we have a conversationId (cold reload / refresh).
  // Without this the customer sees an empty chat after refresh even though the
  // server has all their messages in the DB.
  useEffect(() => {
    if (!conversationId) return
    let cancelled = false
    fetch(`${apiUrl}/public/stores/${storeId}/chat/conversations/${conversationId}`, {
      cache: 'no-store',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return
        const msgs = (data?.messages ?? []) as Message[]
        setMessages(msgs)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [conversationId, storeId, apiUrl])

  // Init socket when open + prechat done
  useEffect(() => {
    if (!open || !prechatDone) return

    // Derive WebSocket base URL: strip trailing /api if present
    const wsBase = apiUrl.replace(/\/api\/?$/, '')

    const socket = io(`${wsBase}/chat`, { withCredentials: true })
    socketRef.current = socket
    setConnecting(true)

    socket.on('connect', () => {
      setConnecting(false)
      if (conversationId) {
        socket.emit('chat:join', { conversationId })
      }
    })

    socket.on('chat:message', ({ message }: { message: Message }) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev
        return [...prev, message]
      })
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [open, prechatDone, apiUrl]) // intentionally exclude conversationId to avoid re-connecting

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, open])

  const startConversation = async (name: string, email: string) => {
    setConnecting(true)
    try {
      const url = `${apiUrl}/public/stores/${storeId}/chat/conversations`
      const payload = {
        visitorName: name || undefined,
        visitorEmail: email || undefined,
        message: config.greeting,
        channel: 'LIVE_CHAT',
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const responseText = await res.text()
      if (!res.ok) throw new Error('Failed')
      const data = responseText ? JSON.parse(responseText) : {}
      const cid = data.id ?? data.conversationId
      setConversationId(cid)
      localStorage.setItem(
        `chat_session_${storeId}`,
        JSON.stringify({ conversationId: cid, visitorName: name, visitorEmail: email }),
      )
      socketRef.current?.emit('chat:join', { conversationId: cid })
      // Add greeting as first message
      setMessages([
        {
          id: 'greeting',
          sender: 'MERCHANT',
          content: config.greeting,
          createdAt: new Date().toISOString(),
        },
      ])
    } catch (e: any) {
    } finally {
      setConnecting(false)
    }
  }

  const handlePrechatSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPrechatDone(true)
    await startConversation(visitorName, visitorEmail)
  }

  const handleSkipPrechat = async () => {
    setPrechatDone(true)
    await startConversation('', '')
  }

  const sendMessage = async () => {
    if (!input.trim() || sending) {
      return
    }
    const content = input.trim()
    setInput('')
    setSending(true)

    // NOTE: we used to add an optimistic message here. We don't anymore
    // because the server now broadcasts chat:message back via the gateway,
    // and adding both would show duplicates. We rely on the broadcast for
    // real-time; for the first message after lazy-create, we use the HTTP
    // response's conversation.messages[0] to render the customer's own
    // first message (the customer emits chat:join AFTER the broadcast).

    try {
      let cid = conversationId
      if (!cid) {
        // No conversation yet — create one with this message as the first.
        // This covers the preChatFormEnabled=false case where the prechat form
        // is skipped and startConversation is never called.
        const startUrl = `${apiUrl}/public/stores/${storeId}/chat/conversations`
        const startRes = await fetch(startUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitorName: visitorName || undefined,
            visitorEmail: visitorEmail || undefined,
            message: content,
            channel: 'LIVE_CHAT',
          }),
        })
        const startBody = await startRes.text()
        if (!startRes.ok) throw new Error('Failed to start conversation')
        const data = startBody ? JSON.parse(startBody) : {}
        cid = data.id ?? data.conversationId
        setConversationId(cid)
        localStorage.setItem(
          `chat_session_${storeId}`,
          JSON.stringify({ conversationId: cid, visitorName, visitorEmail }),
        )
        socketRef.current?.emit('chat:join', { conversationId: cid })
        // Show the customer's own first message immediately (the server-side
        // chat:message broadcast may be lost if the customer joined the room
        // AFTER the broadcast fired — using the HTTP response guarantees the
        // customer sees their own first message).
        const firstMessage = (data?.messages ?? [])[0] as Message | undefined
        if (firstMessage) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === firstMessage.id)) return prev
            return [...prev, firstMessage]
          })
        }
      } else {
        const msgUrl = `${apiUrl}/public/stores/${storeId}/chat/conversations/${cid}/messages`
        const msgRes = await fetch(msgUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content, sender: 'CUSTOMER' }),
        })
        const msgBody = await msgRes.text().catch(() => '')
        // The merchant (and any other open tabs) will receive the message
        // via the chat:message socket broadcast. The customer's own UI will
        // get the same broadcast back through their socket — dedup handles it.
      }
    } catch (e: any) {
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const positionClass = config.position === 'bottom-left'
    ? 'left-5 bottom-5'
    : 'right-5 bottom-5'

  const primary = config.primaryColor ?? '#4F46E5'

  return (
    <div className={`fixed z-50 ${positionClass} flex flex-col items-end gap-3`}>
      {/* Chat window */}
      {open && (
        <div className="w-80 sm:w-96 flex flex-col rounded-2xl shadow-2xl overflow-hidden bg-white border border-gray-200"
             style={{ height: '480px' }}>
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ backgroundColor: primary }}
          >
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                <ChatIcon className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Support</p>
                <p className="text-xs text-white/70">
                  {connecting
                    ? 'Connecting…'
                    : config.isOnline === false
                      ? (config.offlineMessage || 'We will reply soon')
                      : 'Online'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Pre-chat form */}
          {!prechatDone ? (
            <div className="flex-1 flex flex-col justify-center px-5 py-6">
              <p className="text-sm font-semibold text-gray-900 mb-1">Start a conversation</p>
              <p className="text-xs text-gray-500 mb-4">
                Enter your details so we can follow up if needed.
              </p>
              <form onSubmit={handlePrechatSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Your name"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:outline-none"
                  style={{ ['--tw-ring-color' as any]: primary }}
                />
                <input
                  type="email"
                  placeholder="Your email (optional)"
                  value={visitorEmail}
                  onChange={(e) => setVisitorEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:outline-none"
                />
                <button
                  type="submit"
                  className="w-full py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: primary }}
                >
                  Start chat
                </button>
                <button
                  type="button"
                  onClick={handleSkipPrechat}
                  className="w-full py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Skip
                </button>
              </form>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                {messages.length === 0 && !connecting && (
                  <p className="text-xs text-center text-gray-400 mt-4">
                    {config.greeting}
                  </p>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'CUSTOMER' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                        msg.sender === 'CUSTOMER'
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                      style={msg.sender === 'CUSTOMER' ? { backgroundColor: primary } : undefined}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="flex-shrink-0 px-3 py-3 border-t border-gray-100">
                <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message…"
                    className="flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || sending}
                    className="flex-shrink-0 p-1.5 rounded-lg text-white disabled:opacity-40 transition-colors"
                    style={{ backgroundColor: primary }}
                  >
                    <SendIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Launcher button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="h-14 w-14 rounded-full shadow-lg flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95"
        style={{ backgroundColor: primary }}
        aria-label="Open chat"
      >
        {open ? (
          <CloseIcon className="h-6 w-6" />
        ) : (
          <ChatIcon className="h-6 w-6" />
        )}
      </button>
    </div>
  )
}

// ── Inline SVG icons (avoids heroicons dep version issues in storefront) ───────

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  )
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  )
}
